import navItems from '@/constants/navItems';
import roleConstants from '../utilities/enums/rolesTypes';

export default {
    async auth({ next, store }) {
        if (!store.getters.isLoggedIn) {
            await store.dispatch('logout');
            return next({ path: '/login', replace: true });
        }
        return null;
    },
    accessControl({ next, to, store }) {
        // Need review if it still needed. 14-22
        if (to.meta.everyNeededPermissions && !store.getters.hasEveryPermissions(to.meta.everyNeededPermissions)) {
            next(store.getters.defaultUserRoute);
        } else if (to.meta.atLeastNeededRoles && !store.getters.hasAtLeastOneRole(to.meta.atLeastNeededRoles)) {
            next(store.getters.defaultUserRoute);
        }

        const path = to?.path?.split('/')[1];
        if (path) {
            const categoryPath = navItems.find(nav => nav.name === path);
            if (categoryPath && Object.keys(store.state.accessControl.roles).length) {
                const hasAccess = categoryPath.roles.some(role => store.getters.hasRole(role));
                if (hasAccess) {
                    return false;
                }
                next('/clients');
            }
        }
        return null;
    },
    isAdmin({ next, store }) {
        if (!store.getters.hasRole(roleConstants.ADMINISTRATOR)) {
            return next(store.getters.defaultUserRoute);
        }
        return null;
    },
    isNotOperator({ next, store }) {
        if (store.getters.hasRole(roleConstants.OPERATOR)) {
            return next(store.getters.defaultUserRoute);
        }
        return null;
    },
    async isFinancialAdmin({ next, store }) {
        const isSuperAdmin = await store.getters.superAdmin;
        if (store.getters.hasRole(roleConstants.FINANCIALADMINISTRATOR) || isSuperAdmin) {
            return null;
        }
        return next(store.getters.defaultUserRoute);
    },
    async isSuperAdmin({ next, store }) {
        const isSuperAdmin = await store.getters.superAdmin;
        if (isSuperAdmin) {
            return null;
        }
        return next(store.getters.defaultUserRoute);
    },
    async showContract({ next, store }) {
        const isContractFeature = await store.getters.showContractByFeature;
        if (isContractFeature) {
            return null;
        }
        return next(store.getters.defaultUserRoute);
    },
    checkLogin({ next, store }) {
        if (store.getters.isLoggedIn) {
            return next({ name: store.getters.defaultUserRoute });
        }
        return null;
    },
    requiresSelectedTeam({ next, store }) {
        if (!store.getters.hasSelectedTeam) {
            return next(store.getters.defaultUserRoute);
        }
        return null;
    },
    switchTeam({ next, store }) {
        if (store.getters.teamsCount < 2) {
            return next(store.getters.defaultUserRoute);
        }
        return null;
    },
    async decodeURLSignUpData({ next, to, store }) {
        if (to.query.url) {
            const dataValid = await store.dispatch('decodeUrlInviteData', to.query.url);
            if (dataValid) {
                const canSignup = await store.dispatch('verifySignupToken', to.query.url);
                if (canSignup) {
                    return null;
                }
            }
        }
        return next('login');
    },
    async decodeURLInviteData({ next, to, store }) {
        if (to.query.url) {
            const dataValid = await store.dispatch('decodeUrlInviteData', to.query.url);
            if (dataValid) {
                return null;
            }
        }
        return next('login');
    },
    async decodeUrlResetPwdData({ next, to, store }) {
        const canResetPwd = await store.dispatch('decodeUrlResetPwd', to.query.url);
        if (!canResetPwd) {
            return next('login');
        }
        return null;
    },
    async dropTokens({ store }) {
        await store.dispatch('logout');
    },
    async resetInfo({ to, store }) {
        const dispatches = [];

        // We reset the data in the store when they are not longer relevent to the route
        if (!to.params.clientId && Object.keys(store.state.client).length > 0) {
            dispatches.push('resetClientInfo');
        }

        if (
            (!to.params.siteId
            && Object.keys(store.state.site).length > 0)
            || store.state.site.id !== to.params.siteId
        ) {
            dispatches.push('resetSiteInfo');
        }

        if (!to.params.accessGroupId && Object.keys(store.getters.accessGroup).length > 0) {
            dispatches.push('resetAccessGroupInfo');
        }
        if (!to.params.networkTypeId && Object.keys(store.state.networkType).length > 0) {
            dispatches.push('resetNetworkTypeInfo');
        }
        if (!to.params.componentTypeId && Object.keys(store.state.componentType).length > 0) {
            dispatches.push('resetComponentTypeInfo');
        }
        if (!to.params.standardFamilyId && Object.keys(store.state.standardFamily).length > 0) {
            dispatches.push('resetStandardFamilyInfo');
        }
        if (!to.params.productId && Object.keys(store.getters.product).length > 0) {
            dispatches.push('resetProductInfo');
        }
        if (!to.params.memberId && Object.keys(store.state.member).length > 0) {
            dispatches.push('resetMemberInfo');
        }
        if (!to.params.contactId && Object.keys(store.state.contact).length > 0) {
            dispatches.push('resetContactInfo');
        }
        if (!to.params.woTemplateId && Object.keys(store.state.woTemplate).length > 0) {
            dispatches.push('resetWOTemplateInfo');
        }
        if (!to.params.workOrderId && Object.keys(store.getters.workOrder).length > 0) {
            dispatches.push('resetWorkOrderInfo');
        }
        if (!to.params.componentId && Object.keys(store.state.component).length > 0) {
            dispatches.push('resetComponentInfo');
        }
        if (!to.params.analysisTypeId && Object.keys(store.state.analysisType).length > 0) {
            dispatches.push('resetAnalysisTypeInfo');
        }
        if (!to.params.networkId && Object.keys(store.state.network).length > 0) {
            dispatches.push('resetNetworkInfo');
        }
        if (!to.params.riskAnalysisId && Object.keys(store.state.riskAnalysis).length > 0) {
            dispatches.push('resetRiskAnalysisInfo');
        }
        if (!to.params.documentId && Object.keys(store.getters.siteDocument).length > 0) {
            dispatches.push('resetSiteDocument');
        }
        if (!to.params.assessmentId && Object.keys(store.state.assessment).length > 0) {
            dispatches.push('resetLegionellaRiskAssessment');
        }

        dispatches.forEach(async dispatchName => {
            await store.dispatch(dispatchName);
        });
    },
    async getClientInfo({ to, from, store }) {
        if (from.params.clientId !== to.params.clientId) {
            await store.dispatch('getClientInfo', to.params.clientId);
        }
    },
    async getSiteInfo({ to, from, store }) {
        if (from.params.siteId !== to.params.siteId) {
            await store.dispatch('getSiteInfo', to.params.siteId);
        }
    },
    isSiteIncludedInClient({ next, to, from, store }) {
        if (from.params.clientId !== to.params.clientId || from.params.siteId !== to.params.siteId) {
            if (store.getters.client.sites?.length && !store.getters.client.sites?.find(site => site.id === to.params.siteId)) {
                next('notFound404');
            }
        }
    },
    async getNetworkTypeInfo({ to, from, store }) {
        if (from.params.networkTypeId !== to.params.networkTypeId) {
            await store.dispatch('getNetworkTypeInfo', to.params.networkTypeId);
        }
    },
    async getComponentTypeInfo({ to, from, store }) {
        if (from.params.componentTypeId !== to.params.componentTypeId) {
            await store.dispatch('getComponentTypeInfo', to.params.componentTypeId);
        }
    },
    async getStandardFamilyInfo({ to, from, store }) {
        if (from.params.standardFamilyId !== to.params.standardFamilyId) {
            await store.dispatch('getStandardFamilyInfo', to.params.standardFamilyId);
        }
    },
    async getProductInfo({ to, from, store }) {
        if (from.params.productId !== to.params.productId) {
            await store.dispatch('getProductInfo', to.params.productId);
        }
    },
    async getMemberInfo({ to, from, store }) {
        if (from.params.memberId !== to.params.memberId) {
            await store.dispatch('getMemberInfo', to.params.memberId);
        }
    },
    async getContactInfo({ to, from, store }) {
        if (from.params.contactId !== to.params.contactId) {
            await store.dispatch('getContactInfo', to.params.contactId);
        }
    },
    async getWOTemplateInfo({ to, from, store }) {
        if (from.params.woTemplateId !== to.params.woTemplateId) {
            await store.dispatch('getWOTemplateInfo', to.params.woTemplateId);
        }
    },
    async getWorkOrderInfo({ to, from, store }) {
        if (from.params.workOrderId !== to.params.workOrderId) {
            await store.dispatch('getWorkOrderInfo', to.params.workOrderId);
        }
    },
    async getComponentInfo({ to, from, store }) {
        if (from.params.componentId !== to.params.componentId) {
            await store.dispatch('getComponentInfo', to.params.componentId);
        }
    },
    async getLabWorkOrder({ to, from, store }) {
        if (from.params.labWorkOrderId !== to.params.labWorkOrderId) {
            await store.dispatch('getLabWorkOrder', to.params.labWorkOrderId);
        }
    },
    async getAnalysisTypeInfo({ to, from, store }) {
        if (from.params.analysisTypeId !== to.params.analysisTypeId) {
            await store.dispatch('getAnalysisTypeInfo', to.params.analysisTypeId);
        }
    },
    async getNetworkInfo({ to, from, store }) {
        if (from.params.networkId !== to.params.networkId) {
            await store.dispatch('getNetworkInfo', to.params.networkId);
        }
    },
    async getRiskAnalysisInfo({ to, from, store }) {
        if (from.params.riskAnalysisId !== to.params.riskAnalysisId) {
            await store.dispatch('getRiskAnalysisInfo', to.params.riskAnalysisId);
        }
    },
    async getAccessGroupInfo({ to, from, store }) {
        if (from.params.accessGroupId !== to.params.accessGroupId) {
            await store.dispatch('getAccessGroupInfo', to.params.accessGroupId);
        }
    },
    async getSiteDocument({ to, from, store }) {
        const { siteId, documentId } = to.params;

        if (from.params.documentId !== documentId) {
            await store.dispatch('getSiteDocument', { siteId, documentId });
        }
    },
    async getClientProductInfo({ to, from, store }) {
        const { clientId, productId } = to.params;

        if (from.params.productId !== productId) {
            await store.dispatch('getClientProductInfo', { clientId, productId });
        }
    },
    async getSiteProductInfo({ to, from, store }) {
        const { siteId, productId } = to.params;

        if (from.params.productId !== productId) {
            await store.dispatch('getSiteProductInfo', { siteId, productId });
        }
    },
    async getLegionellaRiskAssessment({ to, from, store }) {
        const { assessmentId } = to.params;

        if (from.params.assessmentId !== assessmentId) {
            await store.dispatch('getLegionellaRiskAssessment', assessmentId);
        }
    },
    async getActiveOrders({ to, from, store }) {
        const { siteId } = to.params;
        if (from.params.siteId !== to.params.siteId) {
            await store.dispatch('getOrderItems', { siteId });
        }
    },
    async getUnacknowledgedAlerts({ to, from, store }) {
        if (from.params.siteId !== to.params.siteId) {
            await store.dispatch('getUnacknowledgedAlerts');
        }
    },
};
